/**
 * Utility functions for formatting data in the UI
 */

/**
 * Format confidence score as percentage
 */
export function formatConfidence(confidence: number): string {
  return `${(confidence * 100).toFixed(0)}%`;
}

/**
 * Format date string to readable format
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format datetime string to readable format
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format duration in seconds to human-readable format
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format session status with color
 */
export function getStatusColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active':
      return 'success';
    case 'completed':
      return 'default';
    case 'failed':
      return 'error';
    default:
      return 'warning';
  }
}

/**
 * Format field name to readable label
 */
export function formatFieldName(fieldName: string): string {
  return fieldName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format array as comma-separated string
 */
export function formatArray(arr: any[]): string {
  if (!Array.isArray(arr)) return '';
  return arr.join(', ');
}

/**
 * Format HbA1c value
 */
export function formatHbA1c(value: number | string): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return `${numValue.toFixed(1)}%`;
}

/**
 * Format glucose value (mg/dL)
 */
export function formatGlucose(value: number | string): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return `${numValue} mg/dL`;
}

/**
 * Format blood pressure
 */
export function formatBloodPressure(systolic: number, diastolic: number): string {
  return `${systolic}/${diastolic} mmHg`;
}

/**
 * Truncate long text with ellipsis
 */
export function truncate(text: string, maxLength: number = 100): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Strip markdown from summary text for clean display in Summary / Current Visit Summary panels.
 * Removes # headers, **bold**, and normalizes list markers so raw LLM output is readable.
 */
export function formatSummaryText(raw: string | null | undefined): string {
  if (raw == null || typeof raw !== 'string') return '';
  let s = raw.trim();
  const thinkTag = 'think';
  // Remove model reasoning / CoT blocks if leaked into UI (multiple provider tag shapes).
  const reasoningStrip: RegExp[] = [
    /<redacted_thinking>[\s\S]*?<\/redacted_thinking>/gi,
    new RegExp(`<${thinkTag}>[\\s\\S]*?</${thinkTag}>`, 'gi'),
    new RegExp(`<redacted_thinking>[\\s\\S]*?</${thinkTag}>`, 'gi'),
  ];
  for (const re of reasoningStrip) {
    s = s.replace(re, '');
  }
  s = s
    .replace(/<\/redacted_thinking>/gi, '')
    .replace(/<redacted_thinking>/gi, '')
    .replace(new RegExp(`</?${thinkTag}>`, 'gi'), '')
    .trim();
  // Remove markdown code fences
  s = s.replace(/^```[\w]*\n?/g, '').replace(/\n?```$/g, '');
  // Headers: ### ## # at start of line
  s = s.replace(/^#{1,6}\s*/gm, '');
  // Bold **text** or __text__
  s = s.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/__([^_]+)__/g, '$1');
  // Bullet lines: - or * at start of line
  s = s.replace(/^[\s]*[-*]\s+/gm, '• ');
  // Extra blank lines
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
}

/** Max length for a line to be treated as a section heading (e.g. "Medication:" or "Symptoms:") */
const SECTION_HEADING_MAX_LEN = 60;

/**
 * Parse dynamic sectioned summary (e.g. "Medication:\nShe reports...\n\nSymptoms:\nShe denies...").
 * Returns array of { title, content } for rendering as bold heading + paragraph in Current Visit Summary.
 * Handles: "Title:\ncontent", "Title: content on same line", and "**Title:**\ncontent".
 */
export function parseSummarySections(raw: string | null | undefined): { title: string; content: string }[] {
  if (raw == null || typeof raw !== 'string') return [];
  let text = formatSummaryText(raw);
  if (!text.trim()) return [];

  const sections: { title: string; content: string }[] = [];
  const lines = text.split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const headingMatch = line.match(/^\s*([^:]+):\s*(.*)$/);
    const looksLikeHeading =
      headingMatch &&
      headingMatch[1].trim().length > 0 &&
      headingMatch[1].trim().length <= SECTION_HEADING_MAX_LEN;

    if (looksLikeHeading) {
      const title = headingMatch![1].trim();
      const sameLineContent = (headingMatch![2] || '').trim();
      const contentLines: string[] = sameLineContent ? [sameLineContent] : [];
      i++;
      while (i < lines.length) {
        const next = lines[i];
        const nextMatch = next.match(/^\s*([^:]+):\s*(.*)$/);
        const nextIsHeading =
          nextMatch &&
          nextMatch[1].trim().length > 0 &&
          nextMatch[1].trim().length <= SECTION_HEADING_MAX_LEN;
        if (nextIsHeading) {
          i--;
          break;
        }
        contentLines.push(next);
        i++;
      }
      const content = contentLines.join('\n').trim();
      sections.push({ title, content });
    }
  }

  if (sections.length === 0 && text.trim()) {
    sections.push({ title: 'Summary', content: text.trim() });
  }
  return sections;
}
